import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { useRouter } from 'next/router';

import type { ResultOf } from '@savvy/deprecated-shared/gql-compat';
import { graphql as tadaGraphql } from '@savvy/deprecated-shared/gql-compat';
import { useQueryWithErrorLogging } from '@savvy/deprecated-shared/hooks/useQueryWithErrorLogging';
import { assert } from '@savvy/utils/assert';

const ClientHouseholdQuery = tadaGraphql(/* GraphQL */ `
  query ClientHousehold($salesforceAccountId: String!) {
    household(salesforceAccountId: $salesforceAccountId) {
      id
      demo
      householdStage
    }
  }
`);

export type Household = NonNullable<NonNullable<ResultOf<typeof ClientHouseholdQuery>>['household']>;

type HouseholdContextType = {
  household: Household | undefined;
};

const HouseholdContext = createContext<HouseholdContextType | null>(null);

type HouseholdProviderProps = {
  readonly children: ReactNode;
};

export function HouseholdProvider({ children }: HouseholdProviderProps) {
  const router = useRouter();
  const salesforceAccountId = router.query['salesforceAccountId'];
  assert(
    typeof salesforceAccountId === 'string' || salesforceAccountId === undefined,
    `Invalid salesforceAccountId: expected string`,
  );

  const { data } = useQueryWithErrorLogging(ClientHouseholdQuery, {
    variables: {
      salesforceAccountId: `${salesforceAccountId}`,
    },
    skip: !salesforceAccountId,
  });

  const value = useMemo(
    () => ({
      household: data?.household,
    }),
    [data?.household],
  );

  return <HouseholdContext.Provider value={value}>{children}</HouseholdContext.Provider>;
}

export function useHousehold(): HouseholdContextType {
  const context = useContext(HouseholdContext);
  if (!context) {
    return { household: undefined };
  }

  return context;
}
